import { useEffect, useState } from "react";
import { Users, Swords, Crown, Trophy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getTournamentBracket } from "@/lib/api";

function MatchCard({ match }) {
  const a = match.team_a || {};
  const b = match.team_b || {};
  const winner = match.winner; // "a" | "b" | null
  const hasScores = match.score_a != null && match.score_b != null;

  const rowCls = (isWinner) =>
    `flex items-center justify-between gap-3 px-3 py-2 border-l-2 ${
      isWinner
        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
        : winner
        ? "border-white/5 text-zinc-500"
        : "border-white/10 text-zinc-300"
    }`;

  return (
    <div
      data-testid={`bracket-match-${match.id}`}
      className="tgl-card cut-corners w-56 relative"
    >
      {match.status === "live" && (
        <span className="absolute -top-2 left-2 text-[9px] font-bold uppercase tracking-widest bg-emerald-500 text-black px-2 py-0.5 animate-pulse">
          Live
        </span>
      )}
      <div className={rowCls(winner === "a")}>
        <div className="flex items-center gap-2 min-w-0">
          {a.seed != null && (
            <span className="text-[10px] text-zinc-500 font-bold w-5">
              #{a.seed}
            </span>
          )}
          <span className="font-display text-xs uppercase tracking-wide truncate">
            {a.name || "TBD"}
          </span>
        </div>
        <span
          className={`font-display font-black ${
            winner === "a" ? "text-[#D4AF37]" : ""
          }`}
        >
          {hasScores ? match.score_a : "—"}
        </span>
      </div>
      <div className="h-px bg-white/5" />
      <div className={rowCls(winner === "b")}>
        <div className="flex items-center gap-2 min-w-0">
          {b.seed != null && (
            <span className="text-[10px] text-zinc-500 font-bold w-5">
              #{b.seed}
            </span>
          )}
          <span className="font-display text-xs uppercase tracking-wide truncate">
            {b.name || "TBD"}
          </span>
        </div>
        <span
          className={`font-display font-black ${
            winner === "b" ? "text-[#D4AF37]" : ""
          }`}
        >
          {hasScores ? match.score_b : "—"}
        </span>
      </div>
    </div>
  );
}

const ROUND_LABEL = {
  1: "Round 1",
  2: "Quarter-final",
  3: "Semi-final",
  4: "Final",
};

function labelForRound(round, totalRounds) {
  // Use final-indexed labels
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return "Final";
  if (fromEnd === 1) return "Semi-final";
  if (fromEnd === 2) return "Quarter-final";
  return `Round ${round}`;
}

function Bracket({ data }) {
  const rounds = data.rounds || [];
  const totalRounds = rounds.length;

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-10 min-w-max">
        {rounds.map((r) => (
          <div
            key={r.round}
            className="flex flex-col justify-around min-h-[420px]"
            data-testid={`bracket-round-${r.round}`}
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              {labelForRound(r.round, totalRounds)}
            </p>
            <div className="flex flex-col gap-4 justify-around h-full">
              {r.matches.map((m) => (
                <MatchCard key={m.id} match={m} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamsList({ teams, slots }) {
  const empty = Math.max(0, slots - teams.length);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {teams.map((t) => (
        <div
          key={t.id}
          data-testid={`team-row-${t.id}`}
          className="tgl-card cut-corners p-4"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-display text-sm font-bold uppercase tracking-wide text-white">
              {t.team_name}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
              Seed #{t.seed}
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-400">
            <span className="text-zinc-300">{t.captain_name}</span>
            {t.members && t.members.length > 0 && (
              <> · {t.members.join(" · ")}</>
            )}
          </p>
        </div>
      ))}
      {Array.from({ length: empty }).map((_, i) => (
        <div
          key={`empty-${i}`}
          className="border border-dashed border-white/10 p-4 cut-corners opacity-60"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-display text-sm font-bold uppercase tracking-wide text-zinc-600">
              Open Slot
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-600">
              Seed #{teams.length + i + 1}
            </span>
          </div>
          <p className="mt-1 text-xs text-zinc-600">Awaiting squad registration</p>
        </div>
      ))}
    </div>
  );
}

export default function BracketDialog({ tournament, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !tournament?.id) return;
    setLoading(true);
    getTournamentBracket(tournament.id)
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, tournament]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`bracket-dialog-${tournament?.id}`}
        className="bg-[#0A0A0A] border border-[#D4AF37]/30 text-white max-w-6xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl lg:text-3xl flex items-center gap-3">
            <Trophy size={22} className="text-[#D4AF37]" />
            {tournament?.name} · Bracket
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {tournament?.format} · {tournament?.prize_pool} prize pool
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-16 text-center text-zinc-500">Loading bracket...</div>
        )}

        {!loading && data && (
          <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-white/10 pb-4">
              <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-zinc-400">
                <span className="flex items-center gap-2">
                  <Users size={14} className="text-[#D4AF37]" />
                  {data.registered ?? 0}/{data.slots ?? tournament?.slots} teams
                </span>
                <span className="flex items-center gap-2">
                  <Swords size={14} className="text-[#D4AF37]" />
                  {tournament?.team_size
                    ? `${tournament.team_size} per squad`
                    : "Solo"}
                </span>
              </div>
              <div>
                <span
                  className={`inline-block text-[10px] font-bold uppercase tracking-[0.3em] px-3 py-1 border ${
                    data.locked
                      ? "border-[#D4AF37]/50 text-[#FFE9A0] bg-[#D4AF37]/10"
                      : "border-white/15 text-zinc-400"
                  }`}
                >
                  {data.locked ? "Bracket Locked" : "Registration Open"}
                </span>
              </div>
            </div>

            {!data.locked && (
              <>
                <div className="border border-[#D4AF37]/20 bg-black/40 cut-corners p-5 text-sm text-zinc-300 flex items-start gap-3">
                  <Crown size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <p>{data.reason}</p>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                    Teams Registered
                  </h4>
                  <TeamsList
                    teams={data.teams || []}
                    slots={data.slots ?? tournament?.slots ?? 0}
                  />
                </div>
              </>
            )}

            {data.locked && (
              <>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                    Matchups
                  </h4>
                  <Bracket data={data} />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                    Squad Roster ({data.teams.length})
                  </h4>
                  <TeamsList teams={data.teams} slots={data.slots} />
                </div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 border-t border-white/5 pt-4">
                  Scores appear once matches go live. Current status:{" "}
                  <span className="text-[#D4AF37]">{tournament?.status}</span>
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
